import React, { useState, useEffect, useCallback } from 'react';
import { User, Mail, Phone, Trash2, Edit, Building2 } from 'lucide-react';
import { getContacts, deleteContact } from '../../../lib/supabase/contacts';
import type { Contact, Organization } from '../../../types/contacts';
import { useEntity } from '../../../hooks/useEntity';
import { useAuthStore } from '../../../store/authStore';
import EditContactForm from './EditContactForm';
import ContactDetails from './ContactDetails';
import DeleteConfirmationModal from '../../ui/DeleteConfirmationModal';

interface ContactListProps {
  onEditContact?: (contact: Contact) => void;
  refreshTrigger?: number;
  defaultType?: 'customer' | 'team' | 'supplier' | 'partner';
  showOnlyType?: 'customer' | 'team' | 'supplier' | 'partner';
}

export default function ContactList({ 
  onEditContact, 
  refreshTrigger = 0,
  defaultType,
  showOnlyType 
}: ContactListProps) {
  const { entity } = useEntity();
  const { user } = useAuthStore();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [deletingContactId, setDeletingContactId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadContacts = useCallback(async () => {
    if (!entity?.id) {
      console.log('ContactList: No entity available yet');
      setLoading(false);
      setContacts([]);
      return;
    }

    try {
      console.log('ContactList: Loading contacts for entity', entity.id);
      setLoading(true);
      setError(null);
      
      try {
        const fetchedContacts = await getContacts(entity.id);
        console.log('ContactList: Fetched contacts:', fetchedContacts?.length || 0, fetchedContacts);
        const filteredContacts = showOnlyType 
          ? (fetchedContacts || []).filter(contact => contact.type === showOnlyType)
          : fetchedContacts || [];
        console.log('ContactList: Filtered contacts:', filteredContacts.length);
        setContacts(filteredContacts);
      } catch (contactsError) {
        console.error('Failed to load contacts:', contactsError);
        setError('Failed to load contacts. Please try again.');
        setContacts([]);
      }
    } catch (err) {
      console.error('Failed to load contacts:', err);
      setError('Failed to load contacts. Please try again.');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [entity?.id, refreshTrigger, showOnlyType]);

  useEffect(() => {
    if (entity?.id) {
      loadContacts();
    } else {
      console.log('ContactList: No entity available');
      setLoading(false);
      setContacts([]);
    }
  }, [loadContacts, refreshTrigger]);

  const handleViewContact = (contact: Contact) => {
    setSelectedContact(contact);
  };

  const handleEditContact = (contactId: string) => {
    setEditingContactId(contactId);
  };

  const handleDeleteContact = (contactId: string) => {
    setDeletingContactId(contactId);
  };

  const confirmDeleteContact = async () => {
    if (!deletingContactId) return;
    
    try {
      setIsDeleting(true);
      await deleteContact(deletingContactId);
      setContacts(contacts.filter(c => c.id !== deletingContactId));
      setDeletingContactId(null);
    } catch (error) {
      console.error('Failed to delete contact:', error);
      setError('Failed to delete contact. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        <p className="text-gray-500 mt-4">Loading contacts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadContacts}
          className="mt-2 text-blue-500 hover:text-blue-600"
        >
          Try Again
        </button>
        {!entity?.id && (
          <p className="mt-4 text-gray-500">
            No entity found. Please make sure you've set up your company profile first.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {contacts.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <User className="w-12 h-12 mx-auto text-gray-400 mb-2" />
          <p className="mb-4">No contacts yet. {entity?.id ? "Add your first contact to get started." : "Please set up your company profile first."}</p>
          {!entity?.id && (
            <p className="text-sm text-yellow-600">
              Note: You need to set up your company profile before adding contacts.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {contacts.map((contact) => (
            <div
              onClick={() => handleViewContact(contact)}
              key={contact.id}
              className="bg-white rounded-lg border border-gray-100 p-4 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-50 p-2 rounded-full">
                    <User className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {contact.first_name} {contact.last_name}
                    </h3>
                    {contact.title && (
                      <p className="text-sm text-gray-500">{contact.title}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    className="text-gray-400 hover:text-blue-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditContact(contact.id);
                    }}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button 
                    className="text-gray-400 hover:text-red-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteContact(contact.id);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {contact.source && contact.source !== 'gekko' && (
                    <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full inline-block mt-1">
                      {contact.source === 'import' ? 'Imported' :
                       contact.source === 'csv' ? 'CSV Import' :
                       contact.source === 'google' ? 'Google Contacts' :
                       contact.source === 'outlook' ? 'Outlook' :
                       contact.source === 'salesforce' ? 'Salesforce' :
                       contact.source === 'hubspot' ? 'HubSpot' :
                       contact.source === 'api' ? 'API' :
                       contact.source === 'manual' ? 'Manual' :
                       contact.source}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="mt-4 space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <Building2 className="w-4 h-4 mr-2" />
                  <span className="capitalize">
                    {contact.organization ? (
                      contact.organization.name || 'Unknown Organization'
                    ) : contact.type === 'customer' ? 'Customer' : contact.type}
                  </span>
                </div>
                {contact.phones?.map((phone) => (
                  <div key={phone.id} className="flex items-center text-sm text-gray-600">
                    <Phone className="w-4 h-4 mr-2" />
                    <span>{phone.phone}</span>
                  </div>
                ))}
                {contact.emails?.map((email) => (
                  <div key={email.id} className="flex items-center text-sm text-gray-600">
                    <Mail className="w-4 h-4 mr-2" />
                    <span>{email.email}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedContact && (
        <ContactDetails
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onEdit={() => {
            setEditingContactId(selectedContact.id);
            setSelectedContact(null);
          }}
        />
      )}

      {editingContactId && (
        <EditContactForm
          contactId={editingContactId}
          onClose={() => setEditingContactId(null)}
          onSuccess={() => {
            setEditingContactId(null);
            loadContacts();
          }}
        />
      )}

      {deletingContactId && (
        <DeleteConfirmationModal
          title="Delete Contact"
          message="Are you sure you want to delete this contact? This action cannot be undone."
          isDeleting={isDeleting}
          onConfirm={confirmDeleteContact}
          onCancel={() => setDeletingContactId(null)}
        />
      )}
    </div>
  );
}